import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { LeaseService } from '../../service/lease.service';
import * as LeaseActions from '../actions/lease.action';

@Injectable()
export class LeaseEffects {
  loadLeases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeaseActions.loadLeases),
      mergeMap(() =>
        this.leaseService.getLeases().pipe(
          map(leases => LeaseActions.loadLeasesSuccess({ leases })),
          catchError(error => of(LeaseActions.loadLeasesFailure({ error }))),
        ),
      ),
    ),
  );

  createLease$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeaseActions.createLease),
      mergeMap(action =>
        this.leaseService.createLease(action.request).pipe(
          map(lease => LeaseActions.createLeaseSuccess({ lease })),
          catchError(error => of(LeaseActions.createLeaseFailure({ error }))),
        ),
      ),
    ),
  );

  updateLease$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeaseActions.updateLease),
      mergeMap(action =>
        this.leaseService.updateLease(action.id, action.request).pipe(
          map(lease => LeaseActions.updateLeaseSuccess({ lease })),
          catchError(error => of(LeaseActions.updateLeaseFailure({ error }))),
        ),
      ),
    ),
  );

  uploadLeaseFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeaseActions.uploadLeaseFile),
      mergeMap(action =>
        this.leaseService.uploadLeaseFile(action.id, action.file).pipe(
          map(() => LeaseActions.uploadLeaseFileSuccess({ id: action.id })),
          catchError(error => of(LeaseActions.uploadLeaseFileFailure({ error }))),
        ),
      ),
    ),
  );

  uploadLeaseFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeaseActions.uploadLeaseFiles),
      mergeMap(action =>
        this.leaseService.uploadLeaseFiles(action.id, action.files).pipe(
          map(() => LeaseActions.uploadLeaseFilesSuccess({ id: action.id })),
          catchError(error => of(LeaseActions.uploadLeaseFilesFailure({ error }))),
        ),
      ),
    ),
  );

  updateLeaseFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeaseActions.updateLeaseFile),
      mergeMap(action =>
        this.leaseService.updateLeaseFile(action.id, action.file).pipe(
          map(() => LeaseActions.updateLeaseFileSuccess({ id: action.id })),
          catchError(error => of(LeaseActions.updateLeaseFileFailure({ error }))),
        ),
      ),
    ),
  );

  viewLeaseFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeaseActions.viewLeaseFile),
      mergeMap(action =>
        this.leaseService.viewLeaseFile(action.fileId).pipe(
          map(blob => LeaseActions.viewLeaseFileSuccess({ fileId: action.fileId, blob })),
          catchError(error => of(LeaseActions.viewLeaseFileFailure({ error }))),
        ),
      ),
    ),
  );

  downloadLeaseFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeaseActions.downloadLeaseFile),
      mergeMap(action =>
        this.leaseService.downloadLeaseFile(action.fileId).pipe(
          map(blob => LeaseActions.downloadLeaseFileSuccess({ fileId: action.fileId, blob })),
          catchError(error => of(LeaseActions.downloadLeaseFileFailure({ error }))),
        ),
      ),
    ),
  );

  deleteLeaseFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeaseActions.deleteLeaseFile),
      mergeMap(action =>
        this.leaseService.deleteLeaseFile(action.fileId).pipe(
          map(() => LeaseActions.deleteLeaseFileSuccess({ fileId: action.fileId })),
          catchError(error => of(LeaseActions.deleteLeaseFileFailure({ error }))),
        ),
      ),
    ),
  );

  reloadLeasesAfterFileUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LeaseActions.uploadLeaseFileSuccess,
        LeaseActions.updateLeaseFileSuccess,
        LeaseActions.uploadLeaseFilesSuccess,
        LeaseActions.deleteLeaseFileSuccess,
      ),
      map(() => LeaseActions.loadLeases()),
    ),
  );

  constructor(
    private actions$: Actions,
    private leaseService: LeaseService,
  ) {}
}
